import { useState, useEffect, useRef } from 'react';
import { API, Auth, Storage } from 'aws-amplify';

import {
    Heading,
    Expander,
    Button,
    Link,
    Text,
    View,
    Collection,
    Rating,
    Loader,
    ExpanderItem,
    TextField,
    Image,
    Flex,
    Badge,
    Tabs,
    TabItem
} from '@aws-amplify/ui-react';

import { useNavigate } from "react-router-dom";
import { AddProfile } from "./AddProfile";
import TaskDonutCharts from './TaskDonutCharts';
import WeeklyActivityBar from './WeeklyActivityBar';
import GrowChart from './GrowChart';
import TaskNudgeModal from './TaskNudgeModal';
import { PostImageOrArt } from './PostArt';
import {
    DAILY_ENGAGE_TARGET,
    DEFAULT_GROW_DAILY_TARGET,
    earnedLapNudges,
    isPostCoveredThroughTomorrow,
    lapNudgeForCompletion,
    utcDayKey,
} from './taskLapModel';

const trimString = (str, numChar) => (str && str.length > numChar) ? str.substring(0, numChar) + '...' : (str || '');

// Constants for repeated object literals
const INITIAL_COMPLETED_TODAY = {
    engage: 0,
    grow: 0,
    post: 0,
    confirmConnect: 0,
};

// Mirrors the `growLimits` block returned by gcGetTasks. `*Used` counts are derived server-side
// from the TASK CONNECT rows themselves — do NOT fall back to the profile's
// connectionsWeeklyLimitCounter / connectionsMonthlyCounter, which can be written to a different
// profile partition than the one being read (docs/grow-connect-limits.md §3/§5).
// weeklyHard starts null so pre-fetch renders fall back to the profile's own
// connectionsWeeklyLimit (per-profile setting) instead of a hardcoded 125.
const INITIAL_GROW_LIMITS = {
    dailyTarget: DEFAULT_GROW_DAILY_TARGET,
    dailyUsed: 0,
    weeklyHard: null,
    weeklyUsed: 0,
    monthlyHard: 500,
    monthlyUsed: 0,
};

// Mirrors gcGetTasks: newConnectAllowance === 0 (growActivity.js exhaustion rule).
// used>=hard is preferred over the fetched newConnectAllowance because the *Used fields
// are optimistically bumped per send, so a user who spends their last allowance and then
// confirms in the same session still gets grow credit. growBlockedUntil widens only.
// supplyEmptyToday (server, docs/grow-connect-limits.md §2.1 phase 3): allowance remained but
// the feed had no deliverable new TASK CONNECT — only confirms are possible, so they count.
function isConnectAllowanceExhausted(limits, blockedUntil, profileWeeklyLimit) {
    const weeklyHard = limits.weeklyHard || profileWeeklyLimit || null;
    return Boolean(blockedUntil)
        || Boolean(limits.supplyEmptyToday)
        || (weeklyHard != null && (limits.weeklyUsed || 0) >= weeklyHard)
        || (limits.monthlyHard != null && (limits.monthlyUsed || 0) >= limits.monthlyHard);
}

export function Tasks({ updateProfiles, aProfile, updateAProfile, profiles, balance, updateBalance }) {
    const navigate = useNavigate();

    const [isMobile] = useState(/iphone|ipad/i.test(navigator.userAgent));
    const [isLoading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    // Keyed by task SK (not array index) so "done" state stays correct across type-filtered
    // tabs and list merges — previously an array indexed by the per-tab filtered position,
    // which pointed at the wrong task outside the Engage tab and was also never actually set.
    const [doneButtons, setDoneButtons] = useState(() => new Set());
    const [repostUrlHasError, setRepostUrlHasError] = useState(false);
    const [postUrlHasError, setPostUrlHasError] = useState(false);
    const [engagePercent, setEngagePercent] = useState(0);
    const [growPercent, setGrowPercent] = useState(0);
    const [growMonthPercent, setGrowMonthPercent] = useState(0);
    const [postPercent, setPostPercent] = useState(0);
    const [lastScheduledFor, setLastScheduledFor] = useState(null);
    // Seed-URL input in the Post tab: paste an article link → backend generates post ideas.
    const [seedUrl, setSeedUrl] = useState("");
    const [seedPending, setSeedPending] = useState(false);
    const [seedMessage, setSeedMessage] = useState(null); // { variation, text } | null
    const [completedToday, setCompletedToday] = useState(INITIAL_COMPLETED_TODAY);

    // Remove localStorage persistence for completedToday

    const [weeklyActivity, setWeeklyActivity] = useState(null);
    const [activityStreak, setActivityStreak] = useState(null);

    // ── Unified lap-nudge machinery (shared by ENGAGE and GROW) ──────────────────────────
    // Previously three independent booleans (pauseNudge25Shown / pauseNudge50Shown /
    // pauseNudgeGrowShown) plus a single-slot modal, which meant: engage and grow could
    // overwrite each other's modal and silently lose one; the guards were read from a stale
    // render closure; and nothing ever reset them, so no nudge fired again after a UTC day
    // rollover or after switching to a different profile.
    //
    // Now: one memory of "already shown" milestones, scoped to (profileUrl, UTC day) so it
    // self-invalidates, held in a ref because nothing renders it and a ref cannot go stale
    // between two rapid DONE clicks. Pending nudges queue instead of overwriting.
    //
    // Two sets, deliberately: `fired` is what we actually showed and is STICKY for the scope
    // (so a server count lagging back below a threshold can't re-celebrate it), while
    // `earned` is RECOMPUTED from the current counts on every sync (so it never accumulates).
    // Accumulating would break profile switching: <Tasks> does not remount when switching
    // profile from within /tasks, so the previous profile's counts are still in state for a
    // moment — folding them in permanently would suppress the new profile's real milestones.
    const nudgeScopeRef = useRef({ key: null, fired: new Set(), earned: new Set() });
    const [nudgeQueue, setNudgeQueue] = useState([]);
    // Latest known daily counts, readable synchronously from a click handler. Kept in step
    // with `completedToday` below; the click handler is what advances it optimistically.
    const completedTodayRef = useRef({ ...INITIAL_COMPLETED_TODAY });

    const nudgeScopeKey = `${aProfile?.profileUrl || ""}|${utcDayKey()}`;

    // Re-scope (and therefore clear) the nudge memory whenever the profile or the UTC day
    // changes, then back-fill every milestone the CURRENT counts have already passed so a
    // reload / profile switch never re-celebrates something the user did earlier.
    const syncNudgeScope = (counts, growTarget, lastScheduledForVal) => {
        const scope = nudgeScopeRef.current;
        if (scope.key !== nudgeScopeKey) {
            scope.key = nudgeScopeKey;
            scope.fired = new Set();
            // Drop anything still queued from the previous scope too, so profile A's
            // celebration can't stay on screen (or pop up) for profile B. The functional
            // no-op keeps React bailing out when the queue is already empty.
            setNudgeQueue((prev) => (prev.length ? [] : prev));
        }
        const earned = new Set();
        earnedLapNudges({ completed: counts.engage, lapSize: DAILY_ENGAGE_TARGET })
            .forEach((variant) => earned.add(`engage:${variant}`));
        earnedLapNudges({ completed: counts.grow, lapSize: growTarget })
            .forEach((variant) => earned.add(`grow:${variant}`));
        // Coverage that already exists (reload, stats refresh, next-day scope reset over a
        // still-standing schedule) is "earned", so only a genuine not-covered → covered
        // transition can ever queue the posts nudge.
        if (isPostCoveredThroughTomorrow(lastScheduledForVal)) earned.add("posts:covered");
        scope.earned = earned;
    };

    // Fire a nudge if this count transition earns one and it has not been shown in this scope.
    // Returns the scope key it claimed (so a failed /update-task can release it), or null.
    const maybeQueueNudge = (kind, previousCompleted, nextCompleted, lapSize) => {
        const variant = lapNudgeForCompletion({ previousCompleted, nextCompleted, lapSize });
        if (!variant) return null;
        const key = `${kind}:${variant}`;
        const scope = nudgeScopeRef.current;
        if (scope.fired.has(key) || scope.earned.has(key)) return null;
        scope.fired.add(key);
        setNudgeQueue((prev) => [...prev, { kind, variant, lapSize }]);
        return key;
    };

    const [growBlockedUntil, setGrowBlockedUntil] = useState(null);
    const [growLimits, setGrowLimits] = useState(INITIAL_GROW_LIMITS);
    // Synchronous mirror of growLimits, same reason as completedTodayRef: setTaskStage reads
    // the quota AFTER an `await` (Auth.currentAuthenticatedUser), so the render closure it
    // captured can be a full click stale. Without this, a confirm clicked while a preceding
    // send's auth call is still in flight evaluates exhaustion against pre-bump usage and
    // silently skips the grow credit. The ref is mutated synchronously at every optimistic
    // write below and re-synced from state after each commit.
    const growLimitsRef = useRef({ ...INITIAL_GROW_LIMITS });
    // Server-derived connection outcome stats (gcGetTasks `growStats`) shown in the Grow tab;
    // same stage-count semantics as the "Connections History" block in SpecificProfile.js.
    const [growStats, setGrowStats] = useState(null);

    // Keep the synchronous counter mirror and the nudge memory in step with whatever the
    // server (or an optimistic click) last said. Runs after commit, so it can never preempt a
    // nudge a click just queued — it only marks milestones as "already celebrated".
    useEffect(() => {
        completedTodayRef.current = { ...INITIAL_COMPLETED_TODAY, ...completedToday };
        syncNudgeScope(
            completedTodayRef.current,
            growLimits.dailyTarget || DEFAULT_GROW_DAILY_TARGET,
            lastScheduledFor
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedToday, growLimits.dailyTarget, nudgeScopeKey, lastScheduledFor]);
    // Re-sync the quota mirror from state after every commit, so a server stats merge (which
    // wins over optimistic values) is what the next click's exhaustion check reads. State is
    // always correct — every setGrowLimits call site is functional. The ref can briefly lag by
    // one bump if a click's mutation interleaves with a prior commit whose passive effect has
    // not flushed yet (React 18 concurrent); it reconverges on the very next commit, and the
    // worst case is the same conservative under-credit this ref exists to narrow, never an
    // over-credit.
    useEffect(() => {
        growLimitsRef.current = { ...growLimits };
    }, [growLimits]);
    const isPrefetchingRef = useRef(false);
    // Stats-call sequencing (see fetchStats): monotonically increasing id so only the latest
    // issued stats request may write state, plus an in-flight marker for burst dedupe.
    const statsRequestSeqRef = useRef(0);
    const statsInFlightRef = useRef(null);
    // Always-current snapshot of `tasks`, readable from async closures without
    // waiting for re-render. Used to detect when a refetch returned nothing for
    // the just-emptied tab so we can schedule a backup retry.
    const tasksRef = useRef([]);
    useEffect(() => {
        tasksRef.current = tasks;
    }, [tasks]);

    // Guards the one-time optimistic "today became active" bump below. Re-synced to whatever
    // the backend last said whenever a fresh weeklyActivity arrives (real fetch or profile switch).
    const todayActivatedRef = useRef(false);
    useEffect(() => {
        if (weeklyActivity && Array.isArray(weeklyActivity.days)) {
            const todayInfo = weeklyActivity.days.find((d) => d.isToday);
            todayActivatedRef.current = Boolean(todayInfo && todayInfo.active);
        }
    }, [weeklyActivity]);

    // The weekly streak bar/counter otherwise only updates on the next full getTasks() fetch,
    // which can be minutes away (or never, if no tab happens to empty out). Once the user hits
    // both daily targets client-side, flip "today" active immediately so it doesn't look stale.
    useEffect(() => {
        if (todayActivatedRef.current) return;
        if (!weeklyActivity || !activityStreak) return;
        const engageMet = (completedToday.engage || 0) >= DAILY_ENGAGE_TARGET;
        // Streak/weekly-strip uses the FIRST lap only — it mirrors the backend's isActiveDay,
        // which counts a day active at one daily target, not at two laps.
        const growMet = (completedToday.grow || 0) >= (growLimits.dailyTarget || DEFAULT_GROW_DAILY_TARGET);
        if (!engageMet || !growMet) return;

        todayActivatedRef.current = true;
        setWeeklyActivity((prev) => {
            if (!prev || !Array.isArray(prev.days)) return prev;
            let changed = false;
            const days = prev.days.map((d) => {
                if (d.isToday && !d.active) {
                    changed = true;
                    return { ...d, active: true };
                }
                return d;
            });
            return changed ? { ...prev, days, activeDaysCount: prev.activeDaysCount + 1 } : prev;
        });
        setActivityStreak((prev) => (prev ? { ...prev, current: (prev.current || 0) + 1 } : prev));
    }, [completedToday.engage, completedToday.grow, growLimits.dailyTarget, weeklyActivity, activityStreak]);

    /*aProfile.followersDynamic=`[
        {
            "date": "2025-04-07",
            "followers": 1434
        },
        {
            "date": "2025-04-08",
            "followers": 1436
        }
    ]`;*/

    // Track which tab is active (0 => Engage, 1 => Post, 2 => Grow)
    const [currentTab, setCurrentTab] = useState(0);

    // Helper for mapping tab index -> string
    const tabMapping = ["engage", "post", "grow"];

    // Applies the stats half of a gcGetTasks response (scope=stats or legacy full). Each field
    // is independently optional — the backend deliberately omits fields on its fail-closed
    // reporting path so the last good values survive a transient read error.
    function applyStatsResponse(dataLoc) {
        if (!dataLoc) return;
        if (typeof dataLoc.engagePercent === "number") {
            setEngagePercent(dataLoc.engagePercent);
        }
        if (typeof dataLoc.growPercent === "number") {
            setGrowPercent(dataLoc.growPercent);
        }
        if (typeof dataLoc.growMonthPercent === "number") {
            setGrowMonthPercent(dataLoc.growMonthPercent);
        }
        if (typeof dataLoc.postPercent === "number") {
            setPostPercent(dataLoc.postPercent);
        }
        if (dataLoc.completedToday) {
            setCompletedToday((prev) => ({ ...prev, ...dataLoc.completedToday }));
        }
        if (dataLoc.growBlockedUntil !== undefined) {
            setGrowBlockedUntil(dataLoc.growBlockedUntil);
        }
        if (dataLoc.lastScheduledFor !== undefined) {
            // Monotonic, same as the /update-task success handler: an eventually-consistent
            // stats refetch must never move the held coverage date backwards.
            const next = dataLoc.lastScheduledFor;
            setLastScheduledFor((prev) => (!prev || (next && next > prev) ? next : prev));
        }
        if (dataLoc.growLimits) {
            // supplyEmptyToday widens only here: the stats call may have read the profile
            // row before the parallel tasks call wrote today's marker, so a stale `false`
            // must not clear the fresh `true` the tasks response set. Only a tasks response
            // (growSupplyEmptyToday, authoritative for this fetch) can turn it back off.
            setGrowLimits((prev) => ({
                ...prev,
                ...dataLoc.growLimits,
                supplyEmptyToday:
                    Boolean(dataLoc.growLimits.supplyEmptyToday) || Boolean(prev.supplyEmptyToday),
            }));
        }
        if (dataLoc.growStats) {
            setGrowStats(dataLoc.growStats);
        }
        if (dataLoc.weeklyActivity) {
            setWeeklyActivity(dataLoc.weeklyActivity);
        }
        if (dataLoc.activityStreak) {
            setActivityStreak(dataLoc.activityStreak);
        }
    }

    // One GET with a single retry — shared by the tasks and stats calls so the endpoint and
    // retry policy can't fork between them. The stats retry waits briefly so a fast 429/5xx
    // doesn't get an instant duplicate of a heavy Lambda invocation.
    async function apiGetWithRetry(path, request, label, retryDelayMs = 0) {
        try {
            return await API.get("gcAPI", path, request);
        } catch (e) {
            console.log(`${label} retrying...`, e.message);
            if (retryDelayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            }
            return API.get("gcAPI", path, request);
        }
    }

    // Stats travel on their own parallel scope=stats call so the task list is NEVER blocked
    // behind the engage-window/streak/percent computation (docs/grow-connect-limits.md §6).
    // Fire-and-forget from getTasks: whenever this lands, the guarded setters apply it; until
    // then the UI keeps its previous (or initial) stats.
    //
    // NO `/new` proxy on this path: against a pre-scope backend the no-proxy route skips the
    // whole task-creation pipeline (creation is keyed to proxy === "new") while still returning
    // the stats fields — so the split is deploy-order safe in SIDE EFFECTS, not just values.
    // A new backend treats no-proxy + scope=stats identically.
    //
    // Sequencing: only the LATEST issued stats request may apply (statsRequestSeqRef), so a
    // slow/retried response can never roll fresh counters backward or paint a previous
    // profile's stats after a profile switch. In-flight dedupe keeps refetch bursts (tab-clear
    // + 4s/9s backups) from stacking redundant heavy stats invocations.
    async function fetchStats(clientPath, authHeaders) {
        if (statsInFlightRef.current === clientPath) return;
        statsInFlightRef.current = clientPath;
        const seq = ++statsRequestSeqRef.current;
        try {
            const requestStats = {
                headers: authHeaders,
                queryStringParameters: { scope: "stats" },
            };
            const dataLoc = await apiGetWithRetry(
                `/get-tasks/${clientPath}`,
                requestStats,
                'stats fetch',
                2000
            );
            if (seq === statsRequestSeqRef.current) {
                applyStatsResponse(dataLoc);
            } else {
                console.log('stats response superseded; dropped');
            }
        } catch (e) {
            console.log('stats fetch failed; keeping last known stats', e.message);
        } finally {
            if (statsInFlightRef.current === clientPath) {
                statsInFlightRef.current = null;
            }
        }
    }

    async function getTasks(forProfile, options = {}) {
        if (!forProfile.profileUrl) return;
        // `merge` adds newly-fetched tasks onto the existing list instead of replacing it, so
        // tasks the user already has open/in-progress in ANY tab are never wiped out just because
        // a DIFFERENT tab ran out and triggered a refetch. `silent` skips the loading spinner and
        // the parallel stats fetch; only the background prefetch (below) is silent — a real
        // refetch still refreshes stats, via the un-awaited fetchStats fired alongside.
        const merge = Boolean(options.merge);
        const silent = Boolean(options.silent);
        const limit = options.limit || 6;
        // Only silent background prefetches dedupe against each other; a real (non-silent)
        // refetch must never be dropped here, or its setLoading(true) would get stuck forever.
        if (merge && silent) {
            if (isPrefetchingRef.current) return;
            isPrefetchingRef.current = true;
        }
        if (!silent) {
            setLoading(true);
        }

        try {
            const userUpdate = await Auth.currentAuthenticatedUser();
            // ONE copy of the load-bearing partition-key path and auth header, shared by the
            // tasks call and the parallel stats call — the backend canonicalizes and
            // ghost-probes off exactly this string, so the two calls must never drift apart.
            const clientPath = `${userUpdate.username}_${encodeURIComponent(forProfile.profileUrl)}`;
            const authHeaders = {
                Authorization: `${userUpdate.signInUserSession.idToken.jwtToken}`
            };

            if (!silent) {
                // Parallel, deliberately un-awaited: tasks render the moment their own (much
                // lighter) scope=tasks call returns; the stats land whenever this resolves.
                // Silent prefetches skip it — they always discarded stats anyway.
                fetchStats(clientPath, authHeaders);
            }

            // Read from the ref (always current, even mid-render) rather than the closed-over
            // `tasks` state, and exclude unconditionally — not just when merging — so the backend
            // never resends a task the user already has open. Previously a full (non-merge)
            // refetch always sent an empty excludeIds, so an in-progress task could vanish and
            // then reappear later, causing it to be worked twice.
            const excludeIds = (tasksRef.current || []).map((task) => task.SK).filter(Boolean);

            const requestTasks = {
                headers: authHeaders,
                queryStringParameters: {
                    limit,
                    excludeIds: excludeIds.join(","),
                    // Feed only — the parallel fetchStats call owns every stats field. An old
                    // (pre-scope) backend ignores this param and returns the full payload; the
                    // stats fields it carries are simply not read here (fetchStats applies its
                    // own no-proxy response), so display stays correct either deploy order.
                    scope: "tasks",
                },
            };

            let data = [];

            try {
                const dataLoc = await apiGetWithRetry(
                    `/get-tasks/${clientPath}/new`,
                    requestTasks,
                    'tasks fetch'
                );
                if (dataLoc.data) {
                    data = dataLoc.data;
                };
                // Fresh supply verdict from the feed itself (see isConnectAllowanceExhausted).
                if (typeof dataLoc.growSupplyEmptyToday === "boolean") {
                    growLimitsRef.current = {
                        ...growLimitsRef.current,
                        supplyEmptyToday: dataLoc.growSupplyEmptyToday,
                    };
                    setGrowLimits((prev) => ({
                        ...prev,
                        supplyEmptyToday: dataLoc.growSupplyEmptyToday,
                    }));
                }
            } catch (e) {
                console.log('something went wrong', e.message);
            }
            //console.log('tasks data:', data);

            // Preload avatarUrl from Storage if needed
            const tasksWithAvatars = await Promise.all(data.map(async (el) => {
                el.oldAvatarUrl = el.avatarUrl;
                if (el.SK.indexOf("TASK CONNECT") > -1) {
                } else if (el.SK.indexOf("TASK POST") > -1) {
                    // do nothing, handled on front-end
                } else if (el.avatarUrl && (el.avatarUrl.startsWith('http://') || el.avatarUrl.startsWith('https://'))) {
                    // Already a signed URL from backend, use as-is
                } else if (el.avatarUrl && el.avatarUrl.indexOf("media.licdn.com") > -1) {
                    // LinkedIn CDN URL, use as-is
                } else if (el.avatarUrl) {
                    // Raw S3 path, needs signing
                    // For all tasks: Use authorUser (avatar is always of the person who created the task/campaign)
                    try {
                        el.avatarUrl = await Storage.get(el.avatarUrl, {
                            level: "protected",
                            identityId: el.authorUser
                        });
                    } catch (error) {
                        // Random default avatar
                        var allAvas = [
                            "0be0ea02e75ea8424ddf",
                            "76ac73b6f9eecc945d8a",
                            "Bogota",
                            "CosmoBlue",
                            "ElephantCat",
                            "Etno",
                            "jiri-terc-66160273",
                            "jonaspetersenfromsisaro",
                            "jvelascoastete",
                            "ken-ratama-16a52718a",
                            "PechorinBloom",
                            "Spanglinga"
                        ];
                        let newSK = allAvas[Math.floor(Math.random() * allAvas.length)];
                        el.avatarUrl = 'avatars/' + newSK + '.png';
                    }
                }
                return el;
            }));

            // Merge unconditionally (base=[] on a non-merge/replace call degrades to the old
            // full-replace behavior) so an in-flight refetch never unmounts/reorders tasks the
            // user is already looking at in another tab.
            setTasks((prev) => {
                const base = merge ? prev : [];
                const existingSks = new Set(base.map((task) => task.SK));
                const additions = tasksWithAvatars.filter(
                    (candidate) => !existingSks.has(candidate.SK)
                );
                const merged = [...base, ...additions];
                // Drop any "done" markers for SKs no longer present so the set can't grow unbounded.
                setDoneButtons((prevDone) => {
                    const validSks = new Set(merged.map((task) => task.SK));
                    const next = new Set();
                    prevDone.forEach((sk) => {
                        if (validSks.has(sk)) next.add(sk);
                    });
                    return next;
                });
                return merged;
            });
        } finally {
            if (merge && silent) {
                isPrefetchingRef.current = false;
            }
            if (!silent) {
                setLoading(false);
            }
        }
    }

    async function setTaskStage(taskItem, stage, elementId) {
        var repostUrl = "";
        var newPostUrl = "";
        if (taskItem.requestType.indexOf("REPOST") > -1) {
            repostUrl = document.getElementById("repostUrl-" + elementId)?.value || "";
        };

        if (taskItem.SK.indexOf("POST") > -1) {
            newPostUrl = document.getElementById("postUrl-" + elementId)?.value || "";
        };

        const user = await Auth.currentAuthenticatedUser();
        const requestSetTaskStage = {
            headers: {
                Authorization: `${user.signInUserSession.idToken.jwtToken}`
            },
            body: {
                taskItem: taskItem,
                newStage: stage
            }
        };

        if (taskItem.requestType.indexOf("REPOST") > -1) {
            if (repostUrl.length > 0) {
                requestSetTaskStage.body.repostUrl = repostUrl;
            } else if (stage !== "rejected") {
                setRepostUrlHasError(true);
                return;
            }
        };

        // The post URL is optional (owner ruling 2026-08-20): a post scheduled natively on
        // LinkedIn has no URL yet — DONE must still count and advance coverage. With no URL
        // the backend skips the validation publish (nothing to validate).
        if (taskItem.SK.indexOf("POST") > -1 && newPostUrl.length > 0) {
            requestSetTaskStage.body.newPostUrl = newPostUrl;
        };

        const optimisticProfile = { ...(aProfile || {}) };
        const previousPost = postPercent;
        // What the UI believed about post coverage BEFORE this action — compared against the
        // server's scheduledFor on success to detect a genuine not-covered → covered flip.
        const prevPostCovered = isPostCoveredThroughTomorrow(lastScheduledFor);
        const previousLastPostDate = aProfile?.lastPostDate || null;
        const previousWeeklyActivity = weeklyActivity ? JSON.parse(JSON.stringify(weeklyActivity)) : null;
        const previousActivityStreak = activityStreak ? { ...activityStreak } : null;
        const previousTodayActivated = todayActivatedRef.current;
        // Whether this action optimistically bumped growLimits — the failure path then reverses
        // the bump with a functional decrement (never a snapshot restore, which would clobber
        // concurrent bumps from other in-flight clicks or a fresher server value).
        let growLimitsBumped = false;
        // Nudge memory key claimed by this click, released again if /update-task fails so the
        // milestone can still be celebrated once the user genuinely crosses it.
        let claimedNudgeKey = null;
        // Which completedToday counters this click bumped — decremented FUNCTIONALLY, one by
        // one, on failure, for the same reason growLimits does below: restoring a
        // click-time snapshot would erase bumps from other in-flight clicks (or a fresher
        // server fetch) that landed after this click's snapshot was taken. That matters more
        // now than it used to — these counts are the sole input to the nudge thresholds.
        const bumpedCountKeys = [];

        // Optimistic UI updates for instant feedback. Use functional updaters
        // so rapid successive clicks accumulate correctly (each click reads
        // the freshest prev value rather than a stale closure).
        // ENGAGE and GROW take the SAME shape here — read the freshest count from the ref,
        // advance it, then let the shared lap model decide whether a nudge is earned. The only
        // difference is the lap size (constant vs server-sent target), which is exactly the
        // limit-counting difference we are keeping.
        if (taskItem.SK.indexOf("TASK CAMPAIGN") > -1 && stage === "attempted") {
            const prevEngageCount = completedTodayRef.current.engage || 0;
            const nextEngageCount = prevEngageCount + 1;
            completedTodayRef.current.engage = nextEngageCount;
            bumpedCountKeys.push("engage");
            setCompletedToday((prev) => ({ ...prev, engage: (prev.engage || 0) + 1 }));
            // No optimistic engagePercent mirror: the donut derives its own lap percent from
            // the count (TaskDonutCharts/getLapState) and only falls back to engagePercent
            // when the count is 0, so a second, differently-bracketed copy of the lap maths
            // here could only ever disagree with it.

            claimedNudgeKey = maybeQueueNudge(
                "engage",
                prevEngageCount,
                nextEngageCount,
                DAILY_ENGAGE_TARGET
            ) || claimedNudgeKey;
        }

        if (
            taskItem.SK.indexOf("TASK CONNECT") > -1 &&
            stage === "pending" &&
            taskItem.stage === "new"
        ) {
            const prevGrowCount = completedTodayRef.current.grow || 0;
            const nextGrowCount = prevGrowCount + 1;
            completedTodayRef.current.grow = nextGrowCount;
            bumpedCountKeys.push("grow");
            setCompletedToday((prev) => ({ ...prev, grow: (prev.grow || 0) + 1 }));
            // Keep the server-derived usage in step until the next fetch, so the donut tooltip and
            // the cap banner don't lag a full request behind. Only a new connection request
            // (new -> pending) consumes quota — confirming one never does. (The old optimistic
            // bump of aProfile.connectionsWeeklyLimitCounter/connectionsMonthlyCounter is gone:
            // nothing renders those stale-partition attributes anymore.)
            growLimitsBumped = true;
            growLimitsRef.current = {
                ...growLimitsRef.current,
                dailyUsed: (growLimitsRef.current.dailyUsed || 0) + 1,
                weeklyUsed: (growLimitsRef.current.weeklyUsed || 0) + 1,
                monthlyUsed: (growLimitsRef.current.monthlyUsed || 0) + 1,
            };
            setGrowLimits((prev) => ({
                ...prev,
                dailyUsed: (prev.dailyUsed || 0) + 1,
                weeklyUsed: (prev.weeklyUsed || 0) + 1,
                monthlyUsed: (prev.monthlyUsed || 0) + 1,
            }));

            // Identical nudge rule to Engage — same shared helper, same two-lap thresholds,
            // only the lap size differs (server-sent daily target instead of a constant).
            // The HARD weekly/monthly caps are untouched by this: they are enforced
            // server-side and surface separately via growBlockedUntil.
            claimedNudgeKey = maybeQueueNudge(
                "grow",
                prevGrowCount,
                nextGrowCount,
                growLimits.dailyTarget || DEFAULT_GROW_DAILY_TARGET
            ) || claimedNudgeKey;
        }

        if (
            taskItem.SK.indexOf("TASK CONNECT") > -1 &&
            taskItem.stage === "pending" &&
            (stage === "completed" || stage === "rejected")
        ) {
            // Mirror the server field exactly: completedToday.confirmConnect counts CONFIRMED
            // only (gcGetTasks confirmConnectToday); a withdrawal is an outcome for grow credit
            // below but not a "confirm".
            if (stage === "completed") {
                completedTodayRef.current.confirmConnect =
                    (completedTodayRef.current.confirmConnect || 0) + 1;
                bumpedCountKeys.push("confirmConnect");
                setCompletedToday((prev) => ({
                    ...prev, confirmConnect: (prev.confirmConnect || 0) + 1,
                }));
            }
            // Exhausted-day fallback (owner ruling, resolves decision #3): when no new connect
            // allowance remains, EITHER resolution (confirm or withdraw) counts toward the DAILY
            // grow target, so the donut and the grow lap nudges advance live. Quota fields
            // (growLimits.*Used) are NOT bumped — confirming never consumes allowance.
            if (isConnectAllowanceExhausted(growLimitsRef.current, growBlockedUntil, aProfile?.connectionsWeeklyLimit)) {
                const prevGrowCount = completedTodayRef.current.grow || 0;
                const nextGrowCount = prevGrowCount + 1;
                completedTodayRef.current.grow = nextGrowCount;
                bumpedCountKeys.push("grow");
                setCompletedToday((prev) => ({ ...prev, grow: (prev.grow || 0) + 1 }));
                claimedNudgeKey = maybeQueueNudge(
                    "grow", prevGrowCount, nextGrowCount,
                    growLimits.dailyTarget || DEFAULT_GROW_DAILY_TARGET
                ) || claimedNudgeKey;
            }
        }

        if (taskItem.SK.indexOf("TASK POST") > -1 && stage === "attempted") {
            setPostPercent(100);
            optimisticProfile.lastPostDate = new Date().toISOString();
            updateAProfile(optimisticProfile);
            bumpedCountKeys.push("post");
            completedTodayRef.current.post = (completedTodayRef.current.post || 0) + 1;
            setCompletedToday((prev) => ({ ...prev, post: (prev.post || 0) + 1 }));
        }

        const updatedTasks = tasks.filter(task => task.SK !== taskItem.SK);
        setTasks(updatedTasks);

        // Identify which tab the just-completed task belongs to and check if that
        // tab is now empty. We refetch when EITHER the overall list is empty OR
        // the current section is empty, so each tab refreshes independently.
        const tabType = taskItem.SK.indexOf("TASK CAMPAIGN") > -1 ? "CAMPAIGN"
            : taskItem.SK.indexOf("TASK POST") > -1 ? "POST"
            : taskItem.SK.indexOf("TASK CONNECT") > -1 ? "CONNECT"
            : null;
        const sameTypeRemaining = tabType
            ? updatedTasks.filter((t) => t.SK.indexOf("TASK " + tabType) > -1).length
            : updatedTasks.length;
        const shouldRefetch = updatedTasks.length === 0 || sameTypeRemaining === 0;
        // Second-to-last completion in a tab (1 of this type still visible):
        // kick off a silent merge prefetch so new tasks are merged in without
        // clobbering the remaining one. This gives SQS task creation a
        // head start before the user clicks DONE on the final visible task.
        const shouldPrefetch = !shouldRefetch && sameTypeRemaining === 1;

        // If we just cleared the last visible task in this tab, mark loading
        // immediately so the empty "GET MORE TASKS" view doesn't flash between
        // the optimistic clear and the refetch (which would otherwise look like
        // a white screen).
        if (shouldRefetch) {
            setLoading(true);
        } else if (shouldPrefetch) {
            // Fire-and-forget; getTasks() handles the in-flight guard internally.
            getTasks(aProfile, { merge: true, silent: true });
        }
        setRepostUrlHasError(false);
        setPostUrlHasError(false);

        try {
            const data = await API.post("gcAPI", `/update-task`, requestSetTaskStage);
            if (data.res !== "success") {
                console.log(data);
            }
            if (data.res === "success" && data.scheduledFor) {
                // Server value is monotonic (max(now,last)+24h); never let a racing response
                // move the held value backwards.
                setLastScheduledFor((prev) =>
                    !prev || data.scheduledFor > prev ? data.scheduledFor : prev
                );
                // Coverage through tomorrow is guaranteed by the formula -> donut done-state
                // (mirrors gcGetTasks postPercent).
                setPostPercent(100);
                // One-shot "coverage achieved" celebration: only on a genuine
                // not-covered → covered transition, once per (profile, UTC day). Runs
                // POST-success (unlike the optimistic engage/grow claims), so a failed
                // request can never have queued it — no catch-path retraction needed.
                // data.scheduledFor is truthy here, so coveredThrough is never null.
                if (!prevPostCovered && isPostCoveredThroughTomorrow(data.scheduledFor)) {
                    const key = "posts:covered";
                    const scope = nudgeScopeRef.current;
                    if (!scope.fired.has(key) && !scope.earned.has(key)) {
                        scope.fired.add(key);
                        setNudgeQueue((prev) => [
                            ...prev,
                            { kind: "posts", variant: "covered", coveredThrough: data.scheduledFor },
                        ]);
                    }
                }
            }
        } catch (e) {
            console.log(e.message);
            // Rollback optimistic updates
            setPostPercent(previousPost);
            bumpedCountKeys.forEach((key) => {
                setCompletedToday((prev) => ({
                    ...prev,
                    [key]: Math.max(0, (prev[key] || 0) - 1),
                }));
                completedTodayRef.current[key] = Math.max(
                    0,
                    (completedTodayRef.current[key] || 0) - 1
                );
            });
            // Retract the nudge this click earned: pull it back out of the queue (so a
            // celebration never stands for work that didn't persist) AND release the
            // milestone, so crossing it for real still nudges.
            if (claimedNudgeKey) {
                nudgeScopeRef.current.fired.delete(claimedNudgeKey);
                setNudgeQueue((prev) =>
                    prev.filter((n) => `${n.kind}:${n.variant}` !== claimedNudgeKey)
                );
            }
            // Roll back the optimistic quota bump too — otherwise a failed /update-task leaves an
            // inflated weeklyUsed in the donut tooltip and can surface the "cap reached" banner
            // for a connection request that never actually went out. Functional decrement, not a
            // snapshot restore: a snapshot would erase legitimate bumps from other in-flight
            // clicks (or a fresher server fetch) that landed after this click's snapshot.
            if (growLimitsBumped) {
                growLimitsRef.current = {
                    ...growLimitsRef.current,
                    dailyUsed: Math.max(0, (growLimitsRef.current.dailyUsed || 0) - 1),
                    weeklyUsed: Math.max(0, (growLimitsRef.current.weeklyUsed || 0) - 1),
                    monthlyUsed: Math.max(0, (growLimitsRef.current.monthlyUsed || 0) - 1),
                };
                setGrowLimits((prev) => ({
                    ...prev,
                    dailyUsed: Math.max(0, (prev.dailyUsed || 0) - 1),
                    weeklyUsed: Math.max(0, (prev.weeklyUsed || 0) - 1),
                    monthlyUsed: Math.max(0, (prev.monthlyUsed || 0) - 1),
                }));
            }
            setWeeklyActivity(previousWeeklyActivity);
            setActivityStreak(previousActivityStreak);
            todayActivatedRef.current = previousTodayActivated;
            setDoneButtons((prev) => {
                const next = new Set(prev);
                next.delete(taskItem.SK);
                return next;
            });
            updateAProfile({
                ...optimisticProfile,
                lastPostDate: previousLastPostDate,
            });
        }

        // Refetch when the active tab ran out of tasks, not just when the entire
        // task list is empty. Counting from updatedTasks (React state, not the
        // DOM) avoids cross-tab miscounts and the prior second-to-last bug.
        // `merge: true` so tasks still visible/in-progress in OTHER tabs are never
        // wiped out or resent by this refetch (previously a full replace, which is
        // what caused every tab to appear reset and let an in-progress task
        // disappear then reappear later to be worked twice).
        if (shouldRefetch) {
            await getTasks(aProfile, { merge: true });

            // Backend may have triggered async SQS task creation (see gcGetTasks:
            // "No tasks found after polling, SQS processing may still be in
            // progress"). In that case the immediate refetch returns nothing for
            // this tab. Schedule a couple of backup refetches so new tasks appear
            // without the user having to click anything.
            if (tabType) {
                const stillEmpty = () => {
                    const t = tasksRef.current || [];
                    return t.filter((x) => x.SK && x.SK.indexOf("TASK " + tabType) > -1).length === 0;
                };
                const retryDelays = [4000, 9000]; // ~4s, then +5s
                for (const delay of retryDelays) {
                    if (!stillEmpty()) break;
                    await new Promise((r) => setTimeout(r, delay));
                    if (!stillEmpty()) break;
                    await getTasks(aProfile, { merge: true });
                }
            }
        }
    }

    // Post tab: submit a pasted article URL as a post-idea seed. Mirrors the
    // SpecificProfile.js updateProfile() call to /post-updateProfile (same endpoint,
    // same auth header, body keyed by profileId).
    async function submitSeedUrl() {
        const url = (seedUrl || "").trim();
        if (!/^https?:\/\//i.test(url)) {
            setSeedMessage({ variation: "error", text: "Please paste a full link starting with http:// or https://" });
            return;
        }
        setSeedMessage(null);
        setSeedPending(true);
        try {
            const user = await Auth.currentAuthenticatedUser();
            const data = await API.post("gcAPI", `/post-updateProfile/${user.username}`, {
                headers: {
                    Authorization: `${user.signInUserSession.idToken.jwtToken}`
                },
                body: {
                    profileId: aProfile.profileId,
                    postSeedUrls: [url],
                },
            });
            setSeedMessage(
                data && data.seedsCreated === 0
                    ? { variation: "info", text: "This link was already added." }
                    : { variation: "success", text: "Generating post ideas — they'll appear here shortly." }
            );
            setSeedUrl("");
            // Idea generation is async (SQS) — pull the new POST tasks in once they land,
            // mirroring the retryDelays backup-refetch pattern in setTaskStage.
            for (const delay of [8000, 20000]) {
                setTimeout(() => getTasks(aProfile, { merge: true, silent: true }), delay);
            }
        } catch (e) {
            console.log(e.message);
            setSeedMessage({ variation: "error", text: "Couldn't add the link — please try again." });
        } finally {
            setSeedPending(false);
        }
    }

    useEffect(() => {
        // if no selected profile but have profiles, pick the first
        if (!Object.keys(aProfile).length && profiles.length > 0) {
            updateAProfile(profiles[0]);
        }
        // if tasks are empty, fetch for the selected profile
        if (Object.keys(aProfile).length && tasks.length === 0) {
            getTasks(aProfile);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles, updateAProfile, aProfile]);

    // Renders tasks by given type
    function tasksByType(inputTasks, type) {
        let filteredTasks = inputTasks.filter(task => task.SK.indexOf(type) > -1);

        return (
            <Collection
                direction='column'
                type='list'
                items={filteredTasks}
                searchNoResultsFound={
                    <Flex justifyContent="center">
                        <Text>
                            Nothing found, please try starting from <b>Grow</b> tab in <Link href='/settings/'>Settings</Link>.
                        </Text>
                    </Flex>
                }
            >
                {(item, index) => (
                    // Key by SK (unique, stable across re-renders) so removing a task
                    // unmounts its Expander; otherwise React reuses the index-keyed
                    // instance and the next task inherits the previous open state.
                    <View key={item.SK || index} padding="0rem">
                        <Expander
                            className='task-expander'
                            isCollapsible={true}
                            maxWidth="100%"
                            id={"task-" + index}
                        >
                            <ExpanderItem
                                title={
                                    <Flex
                                        wrap="nowrap"
                                        alignItems="center"
                                        justifyContent="flex-start"
                                        // flex-fill instead of width 100%: forcing 100% of the
                                        // trigger left no room for the expander chevron, pushing
                                        // it past the card edge on narrow screens.
                                        style={{ flex: '1 1 0%', minWidth: 0 }}
                                    >
                                        {item.SK.indexOf("TASK POST") > -1 ? (
                                            <PostImageOrArt
                                                src={item.avatarUrl}
                                                seedString={item.SK}
                                                width={50}
                                                height={50}
                                                rounded
                                                alt={item.name}
                                                style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
                                            />
                                        ) : item.avatarUrl && item.avatarUrl.length > 0 ? (
                                            <img
                                                src={item.avatarUrl}
                                                style={{ borderRadius: '50%', maxWidth: '50px', marginTop: '0.5rem', marginBottom: '0.5rem', flexShrink: 0 }}
                                                alt={item.name}
                                            />
                                        ) : (
                                            <Badge backgroundColor="hsl(200, 100%, 90%)">
                                                <Text style={{ fontSize: '30px' }}>
                                                    {item.url
                                                        ? item.url
                                                            .replace('https://www.', '')
                                                            .replace('https://', '')
                                                            .replace('http://', '')[0]
                                                            .toUpperCase()
                                                        : "P"
                                                    }
                                                </Text>
                                            </Badge>
                                        )}
                                        {/* minWidth 0: a flex item can't shrink below its content's
                                            min-content width by default, so isTruncated (nowrap +
                                            ellipsis) never engaged and long titles widened the page. */}
                                        <Text isTruncated={true} style={{ width: '100%', minWidth: 0 }}>
                                            {item.SK.includes("TASK CONNECT")
                                                ? (item.stage === "new" ? <>CONNECT<br /><small>{trimString(item.title || item.headline, 45)}</small></> : <>CONFIRM CONNECT<br /><small>{item.title || item.headline}</small></>)
                                                : item.SK.includes("TASK POST")
                                                    ? <>POST</>
                                                    : <>{item.needsIntro ? " REPOST +" : ""}{item.needsComment ? " COMMENT +" : ""} LIKE [{item.like}] </>
                                            }
                                        </Text>
                                        {"pods" in item && item.pods.length > 0 ? <Badge>{item.pods}</Badge> : null}
                                        <Flex wrap="nowrap" alignItems="center" justifyContent="flex-end" width="35%">
                                            <Text as="span">
                                                <b>Earn</b>{" "}
                                                {item.requestType.indexOf("PAID-") > -1
                                                    ? <>{item.quantity}0$<br />for 50</>
                                                    : item.requestType === "PAID"
                                                        ? <>5$<br />for 25</>
                                                        : item.quantity
                                                }
                                                <Rating
                                                    value={1}
                                                    maxValue={1}
                                                    fillColor="hsl(51, 100%, 50%)"
                                                />
                                            </Text>
                                        </Flex>
                                    </Flex>
                                }
                                value={'initial-' + index}
                                maxWidth="100%"
                            >
                                <Image src={item.screenshot} style={{ maxWidth: '100%' }} />
                                {(item.SK.indexOf("TASK CONNECT") > -1) && (
                                    <>
                                        <Text as="span">
                                            <b>Followers:</b> {item.followers} &nbsp;
                                        </Text>
                                        {(item.location || item.addressWithCountry) && (
                                            <Text as="span">
                                                <b>Location:</b> {item.location ? item.location : item.addressWithCountry ? item.addressWithCountry : "N/A"} &nbsp;
                                            </Text>)}
                                        <br />
                                        <Text as="span">
                                            <b>Headline:</b> {item.headline} &nbsp;
                                        </Text>
                                        {item.companyCurrentSubtitle && item.companyCurrentLogo && (
                                            <>
                                                <br />
                                                <Text
                                                    as="span"
                                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    <b>Company:&nbsp;</b>
                                                    <img
                                                        src={item.companyCurrentLogo}
                                                        alt={item.companyCurrentSubtitle}
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '50%',
                                                            marginRight: '0.5rem'
                                                        }}
                                                    />
                                                    {item.companyCurrentSubtitle}
                                                </Text>
                                            </>
                                        )}
                                        <br />
                                    </>
                                )}
                                <br />
                                {/* Additional logic for different SK conditions */}
                                {item.SK.indexOf("TASK POST") > -1 ? (
                                    <Text as="span">
                                        <PostImageOrArt
                                            src={item.avatarUrl}
                                            seedString={item.SK}
                                            width={320}
                                            height={180}
                                            alt={item.title}
                                            downloadable
                                            style={{ marginBottom: '0.5rem' }}
                                        />

                                        <b>Step 1.</b> Copy suggested {item.category} news post text and link: <br /><br />
                                        <i><b>{item.title}</b><br /><br /> {item.postText}<br /><br /> {item.url}</i><br /><br />
                                        <Link
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                try {
                                                    await navigator.clipboard.writeText(item.title + '\n\n' + item.postText + '\n\n' + item.url);
                                                } catch (err) {
                                                    console.log('Clipboard copy error', err);
                                                }
                                            }}
                                            id={"copy-" + index}
                                            href='#'
                                            rel="noreferrer"
                                        >
                                            Click TO COPY post text
                                        </Link>
                                    </Text>
                                ) : (
                                    <Text as="span">
                                        <b>Step 1.</b> Open {item.SK.indexOf("TASK CONNECT") > -1 ? 'profile' : 'post'} URL:{" "}
                                        <Link
                                            onClick={() => {
                                                setDoneButtons((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(item.SK);
                                                    return next;
                                                });
                                            }}
                                            href={isMobile
                                                ? item.postUrl.replace(/https:\/\/(.+?)\.linkedin\.com/, 'linkedin://')
                                                : item.postUrl}
                                            target='_blank'
                                            rel="noreferrer"
                                        >
                                            OPEN
                                        </Link>
                                    </Text>
                                )}
                                <br /><br />

                                {/* If TASK POST -> step 2. Open LinkedIn. Otherwise show Connect/Like instruction */}
                                {item.SK.indexOf("TASK POST") > -1 ? (
                                    <Text as="span">
                                        <b>Step 2.</b> Open LinkedIn and publish the post:{" "}
                                        <Link
                                            onClick={() => {
                                                setDoneButtons((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(item.SK);
                                                    return next;
                                                });
                                            }}
                                            href={isMobile ? 'linkedin:///feed/' : 'https://www.linkedin.com/feed/'}
                                            target='_blank'
                                            rel="noreferrer"
                                        >
                                            OPEN LinkedIn
                                        </Link>
                                        <br /><br />
                                        <b>Step 3.</b> Provide the post Url <i>(optional — skip it if you
                                        scheduled the post on LinkedIn)</i>:{" "}
                                        <TextField
                                            id={"postUrl-task-" + index}
                                            hasError={postUrlHasError}
                                            errorMessage="please fill in correct LinkedIn post URL"
                                            placeholder="e.g. https://www.linkedin.com/posts/LongString ..."
                                            onChange={() => setPostUrlHasError(false)}
                                        />
                                    </Text>
                                ) : (
                                    <Text as="span">
                                        <b>Step 2.</b>{" "}
                                        {item.SK.indexOf("TASK CONNECT") > -1
                                            ? item.stage === 'new'
                                                ? <>Connect by clicking: <b>Connect</b></>
                                                : <> If you see <b>Pending</b>, please click: <b>Pending</b> & then <b>Withdraw</b> in the popup <Button
                                                    disabled={doneButtons.has(item.SK)}
                                                    type="submit"
                                                    onClick={() => {
                                                        // Guard by stable SK like the CONNECTED button: a withdrawal now
                                                        // carries counter side effects (confirm outcomes credit the grow
                                                        // donut on allowance-exhausted days), so a double-tap inside the
                                                        // pre-await window would double-credit.
                                                        setDoneButtons((prev) => new Set(prev).add(item.SK));
                                                        setTaskStage(item, "rejected", "task-" + index);
                                                    }}
                                                    backgroundColor="red"
                                                    color="white"
                                                >WITHDRAWN</Button> </>
                                            : <>Deliver LIKE by clicking: Like as [{item.like}]</>
                                        }
                                    </Text>
                                )}

                                {/* Possibly deliver COMMENT or REPOST */}
                                {(item.needsComment && ((item.requestType === "FREE") || (item.requestType === "PAID"))) && (
                                    <Text as="span">
                                        <br /><br />
                                        <b>Step 3.</b> Deliver a COMMENT by typing: <br /><br />
                                        <i>{item.comment}</i><br /><br />
                                        <Link
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                try {
                                                    await navigator.clipboard.writeText(item.comment);
                                                } catch (err) {
                                                    console.log('Clipboard copy error', err);
                                                }
                                            }}
                                            id={"copy-" + index}
                                            href='#'
                                            rel="noreferrer"
                                        >
                                            Click TO COPY comment text
                                        </Link>
                                    </Text>
                                )}

                                {item.requestType.indexOf("REPOST") > -1 && (
                                    <>
                                        <br /><br /><b>Step 3.</b> Deliver COMMENT by typing: <br /><br />
                                        <i>{item.comment}</i><br /><br />
                                        <Link
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                try {
                                                    await navigator.clipboard.writeText(item.comment);
                                                } catch (err) {
                                                    console.log('Clipboard copy error', err);
                                                }
                                            }}
                                            id={"copy-" + index}
                                            href='#'
                                            rel="noreferrer"
                                        >
                                            Click TO COPY comment text
                                        </Link>
                                        <br /><br />
                                        <b>Step 4.</b> Deliver REPOST by clicking "Repost", then "...with your thoughts" & typing: <i>{item.intro}</i>
                                        <br /><br />
                                        <b>Step 5.</b> Provide the repost Url:{" "}
                                        <TextField
                                            id={"repostUrl-task-" + index}
                                            hasError={repostUrlHasError}
                                            errorMessage="please fill in correct LinkedIn repost URL"
                                            placeholder="e.g. https://www.linkedin.com/posts/LongString ..."
                                            onChange={() => {
                                                if (!document.getElementById("repostUrl-task-" + index).value) {
                                                    setRepostUrlHasError(true);
                                                } else {
                                                    setRepostUrlHasError(false);
                                                }
                                            }}
                                        />
                                        <br /><br />
                                        <b>Step 6.</b>
                                    </>
                                )}

                                {/* Next step: confirm either POST, CONNECT, or ENGAGE */}
                                {((item.needsComment && ((item.requestType === "FREE") || (item.requestType === "PAID"))) || (item.SK.indexOf("TASK POST") > -1)) ? (
                                    <>
                                        <br /><br /><Text as="span"><b>Step 4. </b></Text>
                                    </>
                                ) : item.requestType.indexOf("REPOST") > -1 ? null : (
                                    <>
                                        <br /><br /><Text as="span"><b>Step 3. </b></Text>
                                    </>
                                )}
                                Confirm {item.SK.indexOf("TASK POST") > -1
                                    ? 'posting'
                                    : item.SK.indexOf("TASK CONNECT") > -1
                                        ? 'connection'
                                        : 'delivery'} by clicking&nbsp;
                                <Button
                                    disabled={doneButtons.has(item.SK)}
                                    type="submit"
                                    onClick={() => {
                                        if (item.requestType === "FREE-REPOST") {
                                            const checkVal = document.getElementById("repostUrl-task-" + index)?.value || "";
                                            if (!checkVal) {
                                                setRepostUrlHasError(true);
                                                return;
                                            }
                                            setRepostUrlHasError(false);
                                        }
                                        if (item.SK.indexOf("TASK CONNECT") > -1) {
                                            // Mark disabled immediately by stable SK so rapid double-clicks
                                            // cannot submit the same task twice.
                                            setDoneButtons((prev) => new Set(prev).add(item.SK));
                                            if (item.stage === 'new') {
                                                setTaskStage(item, "pending", "task-" + index);
                                            } else if (item.stage === 'pending') {
                                                setTaskStage(item, "completed", "task-" + index);
                                            }
                                        } else if (item.SK.indexOf("TASK POST") > -1) {
                                            // URL optional — empty means "scheduled on LinkedIn,
                                            // no URL yet"; DONE still counts.
                                            setPostUrlHasError(false);
                                            setDoneButtons((prev) => new Set(prev).add(item.SK));
                                            setTaskStage(item, "attempted", "task-" + index);
                                        } else {
                                            setDoneButtons((prev) => new Set(prev).add(item.SK));
                                            setTaskStage(item, "attempted", "task-" + index);
                                        }
                                        // Deliberately NOT incrementing the spendable balance
                                        // here. Clicking DONE only CLAIMS the work; gcValidate
                                        // has not checked LinkedIn yet. The stars are credited
                                        // server-side as PENDING and become spendable only once
                                        // validation confirms them.
                                        //
                                        // The old optimistic `updateBalance(balance + quantity)`
                                        // was never rolled back on failure (the rollback block
                                        // above restores seven other pieces of state but not
                                        // this one), and it also fired on the CONNECT
                                        // new -> pending transition where the server credits
                                        // nothing at all -- so the displayed number drifted
                                        // above server truth for the whole session, and the
                                        // campaign stepper's max was bound to that drift.
                                        // See docs/free-campaign-stars-and-validation.md
                                    }}
                                    id={"task-" + index}
                                >
                                    {item.SK.indexOf("TASK CONNECT") > -1
                                        ? (item.stage === 'new' ? 'DONE' : 'CONNECTED')
                                        : 'DONE'
                                    }
                                </Button>
                                {item.SK.indexOf("TASK POST") > -1 ? (
                                    <>
                                        <br />
                                        <Text as="span" fontSize="small">
                                            Posted now or scheduled it on LinkedIn &mdash; either way, DONE
                                            advances your coverage by a day automatically.
                                        </Text>
                                    </>
                                ) : null}
                                <br /><br />
                                {item.SK.indexOf("TASK CONNECT") < 0 || item.stage === 'new' ? (
                                    <Flex wrap="nowrap" alignItems="center" justifyContent="flex-end">
                                        <Button
                                            type="submit"
                                            onClick={() => setTaskStage(item, "rejected", "task-" + index)}
                                            backgroundColor="red"
                                            color="white"
                                        >
                                            SKIP
                                        </Button>
                                    </Flex>
                                ) : null}
                            </ExpanderItem>
                        </Expander>
                    </View>
                )}
            </Collection>
        );
    }

    return (
        <View align="center">
            {Object.keys(aProfile).length > 0 ? (
                <>
                    {aProfile.validated ? (
                        <View maxWidth="1000px" align="left">
                            <WeeklyActivityBar
                                weeklyActivity={weeklyActivity}
                                activityStreak={activityStreak}
                            />
                            <View align="center">
                                <h2>Complete all <br />3 circles daily</h2>
                                <TaskDonutCharts
                                    validatedProfile={[aProfile]}
                                    engagePercent={engagePercent}
                                    engageCompletedToday={completedToday.engage || 0}
                                    engageDailyTarget={DAILY_ENGAGE_TARGET}
                                    growPercent={growPercent}
                                    growCompletedToday={completedToday.grow || 0}
                                    growDailyTarget={growLimits.dailyTarget || DEFAULT_GROW_DAILY_TARGET}
                                    growWeeklyUsed={growLimits.weeklyUsed}
                                    growWeeklyHard={growLimits.weeklyHard || aProfile.connectionsWeeklyLimit}
                                    growMonthlyUsed={growLimits.monthlyUsed}
                                    growMonthlyHard={growLimits.monthlyHard}
                                    growMonthPercent={growMonthPercent}
                                    postPercent={postPercent}
                                    activeTab={tabMapping[currentTab]}
                                />
                                {/*growBlockedUntil ? (
                                    <Text variation="error">
                                        {growBlockedUntil === "next-week"
                                            ? `Weekly connect cap reached (${growLimits.weeklyUsed}/${growLimits.weeklyHard}). New connect requests resume next week — you can still confirm the ones already sent.`
                                            : `Monthly connect cap reached (${growLimits.monthlyUsed}/${growLimits.monthlyHard}). New connect requests pause until next month — you can still confirm the ones already sent.`}
                                    </Text>
                                ) : null*/}
                                {lastScheduledFor && new Date(lastScheduledFor).getTime() > Date.now() ? (
                                    <Text>
                                        Posts scheduled through {new Date(lastScheduledFor).toLocaleDateString()}
                                    </Text>
                                ) : null}
                            </View>
                            <br />
                            {isLoading ? <Loader variation="linear" /> : null}
                            <Tabs
                                spacing="equal"
                                currentIndex={currentTab}
                                onChange={(index) => setCurrentTab(index)}
                            >
                                <TabItem title={isMobile ? "Engage" : "Engage"}>
                                    <><br />{tasksByType(tasks, "CAMPAIGN")}</>
                                </TabItem>
                                <TabItem title={isMobile ? "Post" : "Post"}>
                                    <><br />
                                        <Flex direction="column" gap="0.5rem" marginBottom="1rem">
                                            <TextField
                                                label="Have an article you want post ideas from? Paste the link:"
                                                placeholder="e.g. https://example.com/article"
                                                value={seedUrl}
                                                hasError={seedMessage?.variation === "error"}
                                                errorMessage={seedMessage?.variation === "error" ? seedMessage.text : undefined}
                                                onChange={(e) => setSeedUrl(e.target.value)}
                                            />
                                            <Button
                                                variation="primary"
                                                isLoading={seedPending}
                                                disabled={seedPending || !aProfile.profileId}
                                                onClick={submitSeedUrl}
                                            >
                                                Generate post ideas from this link
                                            </Button>
                                            {seedMessage && seedMessage.variation !== "error" ? (
                                                <Text variation={seedMessage.variation}>{seedMessage.text}</Text>
                                            ) : null}
                                        </Flex>
                                        {tasksByType(tasks, "POST")}
                                    </>
                                </TabItem>
                                <TabItem title={isMobile ? "Grow" : "Grow"}>
                                    <><br />
                                        <GrowChart aProfile={aProfile} />
                                        <br />
                                        {/* Connection progress at a glance — server-derived from the same
                                            rows that drive the quota (gcGetTasks growStats; last 90 days,
                                            docs/grow-connect-limits.md §6). "Accepted" ratios only requests
                                            resolved after the 3-week wait, so unconfirmed ones can't skew it. */}
                                        {growStats ? (
                                            <Flex justifyContent="center" wrap="wrap" gap="0.4rem" marginBottom="0.75rem">
                                                <Badge size="small" variation="info">
                                                    New sent: {growLimits.dailyUsed || 0}/{growLimits.dailyTarget} today
                                                    &nbsp;·&nbsp;{growLimits.weeklyUsed || 0}/{growLimits.weeklyHard} this week
                                                </Badge>
                                                {growStats.confirmed + growStats.withdrawn > 0 ? (
                                                    <Badge size="small" variation="success">
                                                        Connected: {growStats.confirmed} · Withdrawn: {growStats.withdrawn}
                                                        {growStats.acceptedPercent !== null
                                                            ? ` (${growStats.acceptedPercent}% accepted)`
                                                            : ""}
                                                    </Badge>
                                                ) : null}
                                                {growStats.awaitingConfirm > 0 ? (
                                                    <Badge size="small" variation="warning">
                                                        To confirm now: {growStats.awaitingConfirm}
                                                    </Badge>
                                                ) : null}
                                                {growStats.inWaitingPeriod > 0 ? (
                                                    <Badge size="small">
                                                        In 3-week wait: {growStats.inWaitingPeriod}
                                                    </Badge>
                                                ) : null}
                                            </Flex>
                                        ) : null}
                                        {tasksByType(tasks, "CONNECT")}</>
                                </TabItem>
                            </Tabs>

                            {(tasks.length === 0 && !isLoading) ? (
                                balance > 54
                                    ? <View textAlign="center"><br /><br />Please check again in few hours.</View>
                                    : <View textAlign="center"><br /><br />
                                        <Button onClick={() => { window.location.reload(); }}>
                                            GET MORE TASKS
                                        </Button>
                                    </View>
                            ) : null}
                        </View>
                    ) : (
                        <AddProfile
                            updateProfiles={updateProfiles}
                            aProfile={aProfile}
                            updateAProfile={updateAProfile}
                        />
                    )}
                </>
            ) : (
                <View maxWidth="1000px" align="left">
                    <Heading level={3} fontWeight={'bold'} textAlign="center">Tasks</Heading>
                    <View align="center">
                        <Text variation="secondary">
                            <br />
                            Start by adding social media profile to start earning from it.
                            <br />
                        </Text>
                        <br />
                        <Button variation="primary" onClick={() => { navigate("/addProfile") }}>
                            ADD PROFILE
                        </Button>
                    </View>
                    <br /><br />
                </View>
            )}
            {/* One queue, one modal: engage and grow nudges are shown one after another
                instead of overwriting each other when both land in the same batch. */}
            <TaskNudgeModal
                // Keyed so a second queued nudge remounts the modal — otherwise two
                // back-to-back lap-complete nudges would render the second with an
                // already-spent confetti animation.
                key={nudgeQueue[0] ? `${nudgeQueue[0].kind}:${nudgeQueue[0].variant}` : "none"}
                open={nudgeQueue.length > 0}
                kind={nudgeQueue[0]?.kind}
                variant={nudgeQueue[0]?.variant}
                lapSize={nudgeQueue[0]?.lapSize}
                coveredThrough={nudgeQueue[0]?.coveredThrough}
                onClose={() => setNudgeQueue((prev) => prev.slice(1))}
            />
        </View>
    );
}
